<template>
  <div class="playlist-grouped">
    <!-- Top-level (ungrouped) items -->
    <PlaylistItemList
      v-if="ungroupedItems.length"
      :items="ungroupedItems"
      :groups="grupos"
      :current-grupo="null"
      :readonly="isMos"
      @reorder="onReorderSection(null, $event)"
      @move="$emit('move-item', $event)"
      @remove="$emit('remove', $event)"
      @take-item="$emit('take-item', $event)"
      @drag-state-change="onDragState"
    />

    <!-- Story groups -->
    <div v-for="(g, gIdx) in grupos" :key="g.id_grupo" class="playlist-group">
      <div class="playlist-group__header" @click="toggle(g.id_grupo)">
        <div class="playlist-group__title">
          <v-icon size="16" class="playlist-group__folder">
            {{ isCollapsed(g.id_grupo) ? 'mdi-folder' : 'mdi-folder-open' }}
          </v-icon>
          <input
            v-if="editingId === g.id_grupo"
            :ref="`rename-${g.id_grupo}`"
            v-model="editingTitle"
            type="text"
            class="playlist-group__input"
            @click.stop
            @keyup.enter="commitRename(g)"
            @keyup.esc="cancelRename"
            @blur="commitRename(g)"
          />
          <span
            v-else
            class="playlist-group__name"
            :style="isMos ? '' : 'cursor: text'"
            @click="onNameClick(g, $event)"
          >{{ g.dsc_titulo }}</span>
        </div>
        <div class="playlist-group__right" @click.stop>
          <template v-if="!isMos">
            <v-btn icon x-small :disabled="gIdx === 0" title="Subir" @click="moveGroup(gIdx, -1)">
              <v-icon x-small>mdi-arrow-up</v-icon>
            </v-btn>
            <v-btn icon x-small :disabled="gIdx === grupos.length - 1" title="Descer" @click="moveGroup(gIdx, 1)">
              <v-icon x-small>mdi-arrow-down</v-icon>
            </v-btn>
            <v-btn icon x-small title="Renomear" @click="startRename(g)">
              <v-icon x-small>mdi-pencil</v-icon>
            </v-btn>
            <v-btn icon x-small title="Excluir pasta" @click="$emit('delete-group', g.id_grupo)">
              <v-icon x-small>mdi-delete-outline</v-icon>
            </v-btn>
          </template>
          <v-icon size="24" class="playlist-group__chevron" @click.stop="toggle(g.id_grupo)">
            {{ isCollapsed(g.id_grupo) ? 'mdi-menu-down' : 'mdi-menu-up' }}
          </v-icon>
        </div>
      </div>

      <PlaylistItemList
        v-show="!isCollapsed(g.id_grupo)"
        :items="sectionItems(g.id_grupo)"
        :groups="grupos"
        :current-grupo="g.id_grupo"
        :readonly="isMos"
        @reorder="onReorderSection(g.id_grupo, $event)"
        @move="$emit('move-item', $event)"
        @remove="$emit('remove', $event)"
        @take-item="$emit('take-item', $event)"
        @drag-state-change="onDragState"
      />
      <div
        v-show="!isCollapsed(g.id_grupo) && !sectionItems(g.id_grupo).length"
        class="text-caption text--secondary px-8 py-1"
      >
        Pasta vazia — use o menu de cada item para movê-lo para cá.
      </div>
    </div>

    <div v-if="isEmpty" class="sc-empty-state">
      <span class="sc-empty-state__title">Playlist vazia</span>
    </div>
  </div>
</template>

<script>
/**
 * Renders a playlist as ungrouped (top-level) items + collapsible story groups,
 * reusing PlaylistItemList per section. Degrades to a flat list when there are
 * no groups. Within-section reorder is optimistic (local copy); group/move
 * operations emit upward and re-sync on the parent's refetch.
 *
 * Emits: reorder-items({ idGrupo, items }), move-item({ item, idGrupo }),
 *        remove(item), take-item(item), drag-state-change(bool),
 *        rename-group({ idGrupo, titulo }), delete-group(idGrupo),
 *        reorder-groups(order: [id_grupo]).
 */
import PlaylistItemList from './PlaylistItemList.vue'

export default {
  name: 'PlaylistGroupedView',
  components: { PlaylistItemList },
  props: {
    playlist: { type: Object, required: true },
  },
  data() {
    return {
      localItems: [],
      collapsed: {},
      editingId: null,
      editingTitle: '',
      dragging: false,
    }
  },
  computed: {
    grupos() {
      return [...(this.playlist.grupos || [])].sort((a, b) => a.num_ordem - b.num_ordem)
    },
    ungroupedItems() {
      return this.sectionItems(null)
    },
    isEmpty() {
      return !this.localItems.length && !this.grupos.length
    },
    // MOS-origin playlists are newsroom-authoritative: structure is locked, but the
    // operator may still edit item content (which wins until the next newsroom update).
    // Mirrors Viz Trio's author/operate split.
    isMos() {
      return this.playlist?.dsc_origem === 'MOS'
    },
  },
  watch: {
    playlist: {
      immediate: true,
      deep: true,
      handler() {
        // Don't clobber an in-flight drag — re-sync only when idle.
        if (!this.dragging) this.syncItems()
      },
    },
  },
  methods: {
    syncItems() {
      this.localItems = (this.playlist.items || []).map(i => ({ ...i }))
    },
    sectionItems(idGrupo) {
      return this.localItems
        .filter(i => (i.id_grupo == null ? null : i.id_grupo) === idGrupo)
        .sort((a, b) => a.num_ordem - b.num_ordem)
    },
    isCollapsed(id) {
      return !!this.collapsed[id]
    },
    toggle(id) {
      this.$set(this.collapsed, id, !this.collapsed[id])
    },
    onDragState(isDragging) {
      this.dragging = isDragging
      this.$emit('drag-state-change', isDragging)
    },
    onReorderSection(idGrupo, newItems) {
      // Optimistic: renumber this section's items locally so the order holds
      // until the parent's refetch confirms it.
      newItems.forEach((it, i) => {
        const target = this.localItems.find(x => x.id_playlist_item === it.id_playlist_item)
        if (target) target.num_ordem = i + 1
      })
      this.$emit('reorder-items', { idGrupo, items: newItems })
    },
    moveGroup(idx, dir) {
      const order = this.grupos.map(g => g.id_grupo)
      const target = idx + dir
      if (target < 0 || target >= order.length) return
      const [moved] = order.splice(idx, 1)
      order.splice(target, 0, moved)
      this.$emit('reorder-groups', order)
    },
    // One click on the name opens the rename field. The click is only swallowed when
    // it actually starts an edit — on a MOS playlist, where the title is the
    // newsroom's and not ours to change, it falls through to the header and still
    // collapses the folder.
    onNameClick(g, event) {
      if (this.isMos) return
      event.stopPropagation()
      this.startRename(g)
    },
    startRename(g) {
      this.editingId = g.id_grupo
      this.editingTitle = g.dsc_titulo
      // A native input has no `autofocus` re-trigger between renames (the element is
      // recreated per group), so focus is placed once it exists. Selecting the text
      // matches what the operator wants after one click: replace, not append.
      this.$nextTick(() => {
        const ref = this.$refs[`rename-${g.id_grupo}`]
        const el = Array.isArray(ref) ? ref[0] : ref
        if (el) { el.focus(); el.select() }
      })
    },
    // Escape abandons the edit. Without it the only way out is blur, which commits —
    // so a mistyped name had no way back except renaming again.
    cancelRename() {
      this.editingId = null
      this.editingTitle = ''
    },
    commitRename(g) {
      // Enter commits and then blurs, and Escape blurs after clearing — so this runs
      // more than once per edit. Anything but the live edit is a no-op.
      if (this.editingId !== g.id_grupo) return
      const titulo = (this.editingTitle || '').trim()
      this.editingId = null
      if (titulo && titulo !== g.dsc_titulo) {
        this.$emit('rename-group', { idGrupo: g.id_grupo, titulo })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.playlist-group__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sc-space-4);
  // Constant height, sized to hold the 36px rename field. With vertical padding the
  // row grew by 20px the moment editing began, nudging every folder below it down —
  // so the list moved under the cursor of whoever had just clicked. The resting title
  // and the field both centre in the same box, so nothing shifts either way.
  min-height: 40px;
  padding: 0 var(--sc-space-4);
  background: var(--sc-gg-surface-2);
  border-bottom: 1px solid rgba(77, 78, 81, 0.3);
  cursor: pointer;
}

.playlist-group__title {
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  gap: var(--sc-space-4);
  min-width: 0;
}

.playlist-group__folder {
  color: var(--sc-gg-text-secondary) !important;
}

.playlist-group__name {
  font-size: 14px;
  font-weight: var(--sc-weight-medium);
  line-height: 16px;
  letter-spacing: 0.07px;
  color: var(--sc-gg-text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

// The rename field is a real bordered box on the module surface, not the underlined
// Vuetify field this used to be — inside a #1c1c1c header an underline read as the
// row having come apart rather than as an input. Sized from the design: 36px tall,
// and it takes the width the title had.
.playlist-group__input {
  flex: 1 1 auto;
  min-width: 200px;
  max-width: 800px;
  height: 36px;
  // Horizontal only: the height is fixed and line-height centres the text, so the
  // design's 2px vertical does nothing. Right padding rounds 6px up to the 4px scale
  // rather than introducing an off-scale value for one field.
  padding: 0 var(--sc-space-2) 0 var(--sc-space-3);
  background: var(--sc-gg-surface-1);
  border: 1px solid var(--sc-gg-border-100);
  border-radius: var(--sc-radius-md);
  font-family: inherit;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: -0.07px;
  color: var(--sc-gg-text-primary);
  outline: none;
  // No focus recolour. The field only exists while it holds focus, so an accent
  // border would be its permanent state, not feedback — and the design keeps the
  // neutral border in exactly this (focused, caret showing) state.
}

.playlist-group__right {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.playlist-group__chevron {
  color: var(--sc-gg-text-secondary) !important;
}
</style>
